import { useAuth0 } from "@auth0/auth0-react";
import Chart from "react-apexcharts";
import { Divider, Grid, Text, Button, Card, Spacer, Page, Tabs, useTheme } from "@geist-ui/core";
import { useMediaQuery } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useState } from "react";
import TagManager from "react-gtm-module";
import { v4 } from "uuid";
import PrimaryButton, {
  PrimaryButtonV2,
  SecondaryButtonV2,
} from "../../../components/input-elements/buttons";
import LoadingScreen from "../../../components/loading-screen";
import theme from "../../../theme";
import { DashboardType } from "../../../types/dashboard_types";
import {
  CAMPAIGN_TYPE,
  Fund,
  FUND_STAGE,
  FUND_STATUS,
  PROVIDER,
} from "../../../types/fund_types";
import { MatchingDTO, USER_STATE } from "../../../types/generic_types";
import { Investment, INVESTMENT_STATE } from "../../../types/investment_types";
import { News } from "../../../types/vc-platform/news_types";
import API_CLIENT from "../../../utility/api-client";
import NEWS_CLIENT from "../../../utility/news-client";
import {
  INVESTMENT_TYPE,
  Transaction as TransactionType,
} from "../../portal/dashboard/transaction";
import queryString from "query-string";
import { AppContext } from "../direct-router";
import { WalletPositionV2 } from "../../../components/page-components/dashboard/walletPosition";
import { Link, useHistory } from "react-router-dom";
import { Fund as VCPlatformFund } from "../../../types/vc-platform/news_types";
import { InvestmentStatusChipV2 } from "../../../components/data-display/investment-status-chip";
import { InvestmentDetailsDialog } from "../investment-details/investment-details";
import CopyText, {
  CopyTextV2,
} from "../../../components/data-display/copy-text";
import IBAN from "iban";
import AmountUtils from "../../../utility/amount-utils";
import PostIdentButton from "../../../components/page-components/onboarding/postident-button";
import IVDrawer from "../../../components/template-components/iv-drawer";
import VideoIdent from "./videoident";
import { Helmet } from "react-helmet";

import YouSignDialog from "../../../components/dialogs/yousign-dialog";
import InvestmentDetailsDrawer from "./investment-details-drawer";
import { isHiddenFund } from "../../../utility/hidden-funds";

const CustomDonutChart = React.memo(({ series, labels, colors }: { series: number[], labels: string[], colors?: string[] }) => {
    const total = React.useMemo(() => series.reduce((a, b) => a + b, 0), [series]);

    const options: any = React.useMemo(() => ({
        chart: {
            type: 'donut',
            fontFamily: '"Nunito Sans", sans-serif',
            background: 'transparent',
            animations: {
                enabled: true,
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        labels: labels,
        colors: colors || ['#000000', '#333333', '#666666', '#999999'], 
        plotOptions: {
            pie: {
                donut: {
                    size: '72%',
                    labels: {
                        show: true,
                        name: {
                           show: true,
                           fontSize: '10px',
                           fontFamily: 'inherit',
                           color: '#000',
                           offsetY: -5,
                           fontWeight: 600,
                           formatter: function () {
                                return "GESAMTWERT";
                           }
                        },
                        value: {
                            show: true,
                            fontSize: '16px',
                            fontFamily: 'inherit',
                            fontWeight: 700,
                            color: '#000',
                            offsetY: 5,
                            formatter: function (val: any) {
                                return total.toLocaleString("de-DE") + " €"
                            }
                        },
                        total: {
                            show: true,
                            label: 'Gesamtwert',
                            showAlways: true,
                            fontSize: '10px',
                            fontFamily: 'inherit',
                            color: '#000',
                            fontWeight: 600,
                            formatter: function () {
                                return total.toLocaleString("de-DE") + " €"
                            }
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: false 
        },
        tooltip: {
            enabled: true,
            theme: 'dark',
            style: {
                fontSize: '12px',
                fontFamily: 'inherit'
            },
            y: {
                formatter: function(val: any) {
                    return val.toLocaleString("de-DE") + " €"
                }
            }
        },
        stroke: {
            show: true,
            colors: ['#fff'], 
            width: 2
        }
    }), [labels, colors, total]);

    return (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {series.length > 0 ? (
                <Chart options={options} series={series} type="donut" width="100%" height={260} />
            ) : (
                 <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#eaeaea' }}>
                    <Text small>Keine Daten</Text>
                 </div>
            )}
        </div>
    )
});

export function Dashboard() {
  const history = useHistory();
  const queries = queryString.parse(window.location.search);
  const investment_id = queries.investment_id
    ? queries.investment_id.toString()
    : null;

  const isMobile = useMediaQuery('(max-width:900px)');

  const [dashboard, setDashboard] = useState<DashboardType>();
  const [funds, setFunds] = useState<Fund[]>();
  const [vcplatform_funds, setVcPlatform_funds] = useState<VCPlatformFund[]>();
  const [successfullInvestments, setSuccessfullInvestments] =
    useState<Investment[]>();
  const [transactions, setTransactions] = useState<TransactionType[]>();
  const [wallet, setWallet] = useState<Wallet>();
  const appContext = useContext(AppContext);
  const [news, setNews] = useState<News[]>();
  const [active_investment_id, setActiveInvestmentId] = useState<null | string>(
    investment_id
  );
  const [videoident_open, setVideoIdentOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [scroll_top, setScrollTop] = useState(0);
  const [scroll_left, setScrollLeft] = useState(0);

  // State for Chart Tab
  const [chartTab, setChartTab] = useState<'positions' | 'asset_classes'>('positions');
  const [activeTab, setActiveTab] = useState<'investments' | 'positions'>('investments');

  // Helper to aggregate data based on active tab
  const chartData = React.useMemo(() => {
      if (!wallet?.positions) return { series: [], labels: [] };

      // Tab 1: Positions (Top 5)
      if (chartTab === 'positions') {
          const sorted = [...wallet.positions].sort((a: any, b: any) => b.amount - a.amount);
          return {
              series: sorted.slice(0, 5).map((p: any) => p.amount),
              labels: sorted.slice(0, 5).map((p: any) => p.name)
          };
      }

      // Tab 2: Asset Classes (Group by Stage)
      if (chartTab === 'asset_classes') {
          if (!wallet.positionsByStage) return { series: [], labels: [] };
          return {
              series: wallet.positionsByStage.map((p: any) => p.amount),
              labels: wallet.positionsByStage.map((p: any) => p.stage)
          };
      }
      return { series: [], labels: [] };
  }, [wallet, chartTab]);

  let callId = v4();

  type Position = {
    name: string;
    amount: string;
  };

  useEffect(() => {
    const uData = appContext.matchingData;
    TagManager.dataLayer({
      dataLayer: {
        event: "view-dashboard",
        event_id: callId,
        first_name: appContext.user?.natural_person?.forename,
        last_name: appContext.user?.natural_person?.surname,
        phone: uData?.ph,
        email: appContext.userEmail,
      },
    });
    window.scrollTo(0, 0);
    const getDashboard = async () => {
      const uData = appContext.matchingData;
      if (uData == null) return;
      const accessToken = await getAccessTokenSilently({});
      var mData: MatchingDTO = {
        event: "ViewContent",
        eventId: callId,
        sourceUrl: window.location.href,
        em: uData?.em,
        ph: uData?.ph,
        fn: uData?.fn,
        ln: uData?.ln,
      };
      API_CLIENT.postMatching(accessToken, mData);
      API_CLIENT.getDashboard(
        accessToken,
        (rawDashboard: DashboardType) => {
          const dashboard: DashboardType = {
            ...rawDashboard,
            investments: rawDashboard.investments?.filter(
              (i) => !isHiddenFund(i.fundId)
            ) ?? [],
            coInvestments: rawDashboard.coInvestments?.filter(
              (c) => !isHiddenFund(c.fundId)
            ) ?? [],
          };
          setDashboard(dashboard);
          setSuccessfullInvestments(
            dashboard?.investments
              ?.filter(
                (investment) =>
                  investment.state == INVESTMENT_STATE.ACCEPTED ||
                  investment.state == INVESTMENT_STATE.DELIVERED ||
                  investment.state == INVESTMENT_STATE.KYC_INVALID ||
                  investment.state == INVESTMENT_STATE.KYC_PENDING ||
                  investment.state == INVESTMENT_STATE.PAID ||
                  investment.state == INVESTMENT_STATE.WAITING_FOR_ACCEPTANCE ||
                  investment.state == INVESTMENT_STATE.SIGNATURE_PENDING ||
                  investment.state == INVESTMENT_STATE.SIGNED
              )
              .sort((a, b) => {
                var a_time = a.acceptedAt ? a.acceptedAt : a.offerCreatedAt;
                var b_time = b.acceptedAt ? b.acceptedAt : b.offerCreatedAt;

                return new Date(b_time).getTime() - new Date(a_time).getTime();
              })
          );
        },
        (error: string) => {
          setDashboard({
            coInvestments: [],
            investments: [],
            tokens: [],
          });
        }
      );
      API_CLIENT.getFunds(
        accessToken,
        (funds: Fund[]) => {
          setFunds(funds);
        },
        () => {}
      );
    };
    getDashboard();
  }, []);

  useEffect(() => {
    if (dashboard == undefined || dashboard == null) return;
    const successfullInvestments = dashboard.investments
      ?.filter(
        (investment) =>
          investment.state == INVESTMENT_STATE.ACCEPTED ||
          investment.state == INVESTMENT_STATE.DELIVERED ||
          investment.state == INVESTMENT_STATE.KYC_INVALID ||
          investment.state == INVESTMENT_STATE.KYC_PENDING ||
          investment.state == INVESTMENT_STATE.PAID ||
          investment.state == INVESTMENT_STATE.WAITING_FOR_ACCEPTANCE ||
          investment.state == INVESTMENT_STATE.SIGNATURE_PENDING ||
          investment.state == INVESTMENT_STATE.SIGNED ||
          investment.state == INVESTMENT_STATE.WAITING_FOR_YOUSIGN
      )
      .map((investment) => {
        const t: TransactionType = {
          type: INVESTMENT_TYPE.PLATFORM,
          id: investment.id,
          fundId: investment.fundId,
          fundName: investment.fundName,
          campaignId: investment.campaignId,
          state: investment.state,
          signedAt: investment.signedAt ? new Date(investment.signedAt) : null,
          offerCreatedAt: investment.offerCreatedAt
            ? new Date(investment.offerCreatedAt)
            : null,
          amount: investment.amount,
        };
        return t;
      });

    const coInvestments = dashboard.coInvestments?.map((coinv) => {
      const t: TransactionType = {
        type: INVESTMENT_TYPE.COINVEST,
        id: coinv.id,
        fundId: coinv.fundId,
        fundName: coinv.fundName,
        campaignId: coinv.campaignId,
        signedAt: new Date(coinv.investedOn),
        offerCreatedAt: null,
        amount: coinv.amount,
        documents: coinv.documents,
      };
      return t;
    });
    const allTransactions = successfullInvestments
      .concat(coInvestments)
      .sort((a, b) => {
        var a_time = a.signedAt
          ? a.signedAt.getTime()
          : a.offerCreatedAt
          ? a.offerCreatedAt.getTime()
          : 0;
        var b_time = b.signedAt
          ? b.signedAt.getTime()
          : b.offerCreatedAt
          ? b.offerCreatedAt.getTime()
          : 0;

        return b_time - a_time;
      });
    setTransactions(allTransactions);
    /**.sort(
            (a, b) =>
              new Date(b.signedAt).getTime() -
              new Date(a.signedAt).getTime()
          ) */
  }, [dashboard]);

  useEffect(() => {
    if (transactions && funds) {
      setWallet(new Wallet(transactions, funds));
    }
  }, [transactions, funds]);

  useEffect(() => {
    var fundIds = wallet?.funds.map((fund) => fund.targetInvestmentId);
    if (!fundIds) fundIds = [];

    //for debugging new news, this switch allows to show all news by adding ?allnews=true to the query

    NEWS_CLIENT.getFunds(fundIds, (funds: VCPlatformFund[]) => {
      if (funds) {
        setVcPlatform_funds(funds);
      }
    });
  }, [wallet]);

  useEffect(() => {
    if (active_investment_id) {
      disableScroll1();
    } else {
      enableScroll1();
    }
  }, [active_investment_id]);

  if (!dashboard || !funds || !wallet || !transactions)
    return <LoadingScreen className="w-full h-screen" />;

  //creating investment lists
  const actionRequiredInvestments = dashboard?.investments?.filter(
    (investment) =>
      investment.state == INVESTMENT_STATE.ACCEPTED ||
      investment.state == INVESTMENT_STATE.SIGNATURE_PENDING
  );
  const signedAndWaitingForSignedInvestments = dashboard?.investments?.filter(
    (investment) =>
      investment.state == INVESTMENT_STATE.SIGNED ||
      investment.state == INVESTMENT_STATE.SIGNATURE_PENDING
  );

  const pendingInvestments = dashboard?.investments?.filter(
    (investment) => investment.state == INVESTMENT_STATE.PENDING
  );
  const last_pending = pendingInvestments.pop();
  const last_pending_fund = funds.find(
    (fund) => fund.cashlinkId == last_pending?.fundId
  );
  const last_pending_campaign = last_pending_fund?.cashlinkCampaigns.find(
    (campaign) => campaign.id == last_pending?.campaignId
  );
  const is_last_pending_fund_active =
    last_pending_fund?.fundStatus == FUND_STATUS.OPEN;

  const has_open_investment = signedAndWaitingForSignedInvestments.length > 0;
  const needs_videoident =
    appContext.userState == USER_STATE.ONBOARDED_1 && has_open_investment;

  const portfolioSeries = {
    series: wallet?.positions?.map((positions) => positions.amount),
  };
  const stageSeries = {
    series: wallet?.positionsByStage?.map((position) => position.amount),
  };
  const investment_sum = wallet?.positions
    .map((position) => position.amount)
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  const lastPendingIsActive =
    funds.find((fund) => fund.id === last_pending?.fundId)?.fundStatus ==
    FUND_STATUS.OPEN;

  function disableScroll() {
    // Get the current page scroll position in the vertical direction
    var scrollTop = window.scrollY || document.documentElement.scrollTop;

    // Get the current page scroll position in the horizontal direction

    var scrollLeft = window.screenX || document.documentElement.scrollLeft;

    // if any scroll is attempted,
    // set this to the previous value
    window.onscroll = function () {
      window.scrollTo(scrollLeft, scrollTop);
    };
    document.body.style.position = "fixed";
    document.body.style.position = "";
  }

  function enableScroll() {
    window.onscroll = function () {};
  }

  function disableScroll1() {
    // Store the current scroll position
    let scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    setScrollTop(scrollTop);
    setScrollLeft(scrollLeft);

    // Apply styles to disable scrolling and hide the scrollbar
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollTop}px`;
    document.body.style.left = `-${scrollLeft}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
  }

  function enableScroll1() {
    // Remove styles to enable scrolling and show the scrollbar
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
    window.scrollTo(scroll_left, scroll_top);
  }







  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', paddingTop: isMobile ? '100px' : '160px', paddingBottom: '100px' }}>
      <div style={{ maxWidth: '1000pt', margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px' }}>
         
         {needs_videoident && (
           <div style={{ marginBottom: '32px' }}>
              <VideoIdent />
           </div>
         )}

        {/* Top Section: Portfolio & Metrics */}
        <Grid.Container gap={isMobile ? 1 : 2}>
           {/* Left: Portfolio Overview */}
           <Grid xs={24} md={16}>
              <div style={{ 
                  padding: isMobile ? '24px' : '40px', 
                  border: '1px solid #EAEAEA', 
                  borderRadius: '16px', 
                  background: '#fff', 
                  width: '100%',
                  height: '100%',
                  minHeight: '320px',
                  boxShadow: 'none'
              }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Text h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#000' }}>Portfolioübersicht</Text>
                    {/* Tabs */}
                    {!isMobile && (
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <Text 
                            span 
                            onClick={() => setChartTab('asset_classes')}
                            style={{ 
                                fontSize: '13px', 
                                color: chartTab === 'asset_classes' ? '#000' : '#888', 
                                fontWeight: chartTab === 'asset_classes' ? 600 : 400,
                                borderBottom: chartTab === 'asset_classes' ? '2px solid #000' : 'none', 
                                paddingBottom: '4px', 
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontFamily: '"Nunito Sans", sans-serif'
                            }}
                        >
                            Anlageklassen
                        </Text>
                        <Text 
                            span 
                            onClick={() => setChartTab('positions')}
                            style={{ 
                                fontSize: '13px', 
                                color: chartTab === 'positions' ? '#000' : '#888', 
                                fontWeight: chartTab === 'positions' ? 600 : 400,
                                borderBottom: chartTab === 'positions' ? '2px solid #000' : 'none', 
                                paddingBottom: '4px', 
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontFamily: '"Nunito Sans", sans-serif'
                            }}
                        >
                            Positionen
                        </Text>
                    </div>
                    )}
                 </div>

                 <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: '32px' }}>
                    <div style={{ flex: isMobile ? '1' : '0 0 240px', width: '100%' }}>
                        <CustomDonutChart
                            series={chartData.series}
                            labels={chartData.labels}
                            colors={['#000000', '#333333', '#666666', '#999999', '#cccccc']}
                        />
                    </div>
                    <div style={{ flex: 1, width: '100%' }}>
                        {chartData.labels.map((label: string, i: number) => {
                             const total = wallet?.positions.reduce((a: number, b: any) => a + b.amount, 0) || 1;
                             const amount = chartData.series[i];
                             const percent = Math.round((amount / total) * 100);
                             const colors = ['#000000', '#333333', '#666666', '#999999', '#cccccc'];

                             return (
                                 <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                     <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                         <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[i % colors.length] }}></div>
                                         <div>
                                             <Text span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{label}</Text>
                                             {chartTab === 'positions' && (
                                                <Text span style={{ display: 'block', fontSize: '11px', color: '#000' }}>
                                                    {wallet?.positions.find((p:any) => p.name === label)?.stage || 'Venture Capital'}
                                                </Text>
                                             )}
                                         </div>
                                     </div>
                                     <div style={{ textAlign: 'right' }}>
                                         <Text span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{amount.toLocaleString("de-DE")} €</Text>
                                         <Text span style={{ display: 'block', fontSize: '11px', color: '#000' }}>{percent}%</Text>
                                     </div>
                                 </div>
                             )
                        })}
                    </div>
                 </div>
              </div>
           </Grid>

           {/* Right: Metrics Stack */}
           <Grid xs={24} md={8}>
               <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '20px', width: '100%', height: '100%' }}>
                   {/* Card 1: Total Investment */}
                   <div style={{ padding: '24px', background: '#fff', border: '1px solid #EAEAEA', borderRadius: '16px', boxShadow: 'none' }}>
                       <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px', fontFamily: '"Nunito Sans", sans-serif' }}>GESAMTINVESTITION</Text>
                       <Text h2 style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: '#000', fontFamily: '"Nunito Sans", sans-serif', letterSpacing: '-0.03em' }}>{investment_sum?.toLocaleString("de-DE")} €</Text>
                   </div>
                   
                   {/* Card 2: Products */}
                   <div style={{ padding: '24px', background: '#fff', border: '1px solid #EAEAEA', borderRadius: '16px', boxShadow: 'none' }}>
                       <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px', fontFamily: '"Nunito Sans", sans-serif' }}>INVESTIERTE PRODUKTE</Text>
                       <Text h2 style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: '#000', fontFamily: '"Nunito Sans", sans-serif', letterSpacing: '-0.03em' }}>{wallet?.positions?.length || 0}</Text>
                   </div>

                   {/* Card 3: Distributions */}
                   <div style={{ padding: '24px', background: '#fff', border: '1px solid #EAEAEA', borderRadius: '16px', boxShadow: 'none' }}>
                       <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px', fontFamily: '"Nunito Sans", sans-serif' }}>ERHALTENE AUSSCHÜTTUNGEN</Text>
                       <Text h2 style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: '#000', fontFamily: '"Nunito Sans", sans-serif', letterSpacing: '-0.03em' }}>0,00 €</Text>
                   </div>
               </div>
           </Grid>
        </Grid.Container>

        <Spacer h={2} />

        {/* Bottom Section: Tabs for Investments & Positions */}

         <div style={{ marginTop: '40px' }}>
             {/* Custom Tabs Header */}
             <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', borderBottom: '1px solid #EAEAEA', paddingBottom: '0px' }}>
                <div 
                    onClick={() => setActiveTab('investments')}
                    style={{ 
                        paddingBottom: '12px', 
                        borderBottom: activeTab === 'investments' ? '2px solid #000' : '2px solid transparent', 
                        cursor: 'pointer',
                        marginBottom: '-1px'
                    }}
                >
                     <Text span style={{ 
                         fontSize: '16px', 
                         fontWeight: activeTab === 'investments' ? 700 : 500, 
                         color: activeTab === 'investments' ? '#000' : '#888',
                         fontFamily: '"Nunito Sans", sans-serif'
                     }}>Investitionen</Text>
                </div>
                <div 
                    onClick={() => setActiveTab('positions')}
                    style={{ 
                        paddingBottom: '12px', 
                        borderBottom: activeTab === 'positions' ? '2px solid #000' : '2px solid transparent', 
                        cursor: 'pointer',
                        marginBottom: '-1px'
                    }}
                >
                     <Text span style={{ 
                         fontSize: '16px', 
                         fontWeight: activeTab === 'positions' ? 700 : 500, 
                         color: activeTab === 'positions' ? '#000' : '#888',
                         fontFamily: '"Nunito Sans", sans-serif'
                     }}>Positionen & Reportings</Text>
                </div>
             </div>

             {/* Tab Content */}
             {activeTab === 'investments' && (
                <div style={{ 
                    border: '1px solid #EAEAEA', 
                    borderRadius: '16px', 
                    background: '#fff', 
                    overflow: 'hidden',
                    boxShadow: 'none'
                }}>
                    {/* Table Header */}
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: isMobile ? '800px' : '100%' }}>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '2.5fr 1.5fr 1fr 1fr 0.8fr', 
                                padding: '16px 32px', 
                                background: '#fff', 
                                borderBottom: '1px solid #EAEAEA' 
                            }}>
                                <Text small style={{ color: '#000', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: '"Nunito Sans", sans-serif' }}>PRODUKT</Text>
                                <Text small style={{ color: '#000', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: '"Nunito Sans", sans-serif' }}>INVESTITIONSBETRAG</Text>
                                <Text small style={{ color: '#000', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: '"Nunito Sans", sans-serif' }}>DATUM</Text>
                                <Text small style={{ color: '#000', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: '"Nunito Sans", sans-serif' }}>STATUS</Text>
                                <Text small style={{ color: '#000', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right', fontFamily: '"Nunito Sans", sans-serif' }}>AKTIONEN</Text>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {transactions?.map((t: any, i: number) => (
                                    <div 
                                        key={i} 
                                        onClick={() => setActiveInvestmentId(t.id)}
                                        style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: '2.5fr 1.5fr 1fr 1fr 0.8fr',  
                                            padding: '24px 32px', 
                                            alignItems: 'center', 
                                            borderBottom: i === transactions.length - 1 ? 'none' : '1px solid #EAEAEA',
                                            cursor: 'pointer',
                                            transition: 'background 0.1s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <div>
                                            <Text p font="14px" my={0} style={{ fontWeight: 700, color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{t.fundName}</Text>
                                            <Text p font="12px" my={0} style={{ color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{t.type === 0 ? 'Venture Capital' : 'Co-Investment'}</Text>
                                        </div>
                                        <Text p font="14px" my={0} style={{ fontWeight: 700, color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{t.amount.toLocaleString("de-DE")} €</Text>
                                        <Text p font="13px" my={0} style={{ color: '#000', fontFamily: '"Nunito Sans", sans-serif' }}>{t.signedAt ? new Date(t.signedAt).toLocaleDateString() : t.offerCreatedAt ? new Date(t.offerCreatedAt).toLocaleDateString() : '-'}</Text>
                                        <div><InvestmentStatusChipV2 state={t.state || (t.type === 1 ? 'DELIVERED' : '')} /></div>
                                        <div style={{ textAlign: 'right' }}>
                                            <Text span style={{ color: '#000', fontSize: '13px', fontWeight: 600, fontFamily: '"Nunito Sans", sans-serif' }}>Details</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
             )}

             {activeTab === 'positions' && (
                <div style={{ width: '100%' }}>
                    {wallet?.positions.map((position: any, i: number) => (
                            <WalletPositionV2
                                key={i}
                                position={position}
                                fund={vcplatform_funds?.find((f:any) => f.fundId == position.fund?.targetInvestmentId)}
                                style={{ width: '100%' }}
                            />
                    ))}
                    {wallet?.positions.length === 0 && (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#666', border: '1px solid #EAEAEA', borderRadius: '16px', background: '#fff', boxShadow: 'none' }}>
                            <Text p style={{ fontFamily: '"Nunito Sans", sans-serif' }}>Keine aktiven Positionen verfügbar.</Text>
                        </div>
                    )}
                </div>
             )}
         </div>
      
        <InvestmentDetailsDrawer
          investment_id={active_investment_id}
          dashboard={dashboard}
          setInvestmentId={setActiveInvestmentId}
        />
      </div>
    </div>
  );
}

type Stage = {};

export type WalletPositionType = {
  productId: string;
  name: string;
  amount: number;
  stage: string;
  fund: Fund | undefined;
  investments: TransactionType[];
};

type StagePosition = {
  stage: string;
  amount: number;
};

class Wallet {
  stageMap = new Map();

  positions: WalletPositionType[] = [];
  positionsByStage: StagePosition[] = [];
  funds: Fund[] = [];

  constructor(transaction: TransactionType[], funds: Fund[]) {
    console.log(funds);
    this.funds = funds.filter((fund) =>
      transaction.find((transaction) => transaction.fundId == fund.cashlinkId)
    );

    //create mapping for stages
    funds.forEach((fund) => {
      var stage = fund.investmentStage;
      this.stageMap.set(
        fund.cashlinkId,
        FUND_STAGE[stage as string as keyof typeof FUND_STAGE]
      );
    });

    //seed with investments
    transaction.forEach((transaction) => {
      this.putInvestment(transaction);
      this.putInvestmentStage(transaction);
    });

    this.positions.sort((a, b) => b.amount - a.amount);
    this.positionsByStage.sort((a, b) => b.amount - a.amount);
  }

  getFund(fundId: string): Fund | undefined {
    return this.funds.find((fund) => fund.cashlinkId == fundId);
  }

  putInvestment(investment: TransactionType) {
    for (var i = 0; i < this.positions.length; i++) {
      if (this.positions[i].productId == investment.fundId) {
        this.positions[i].name = investment.fundName;
        this.positions[i].investments.push(investment);
        this.positions[i].amount += investment.amount;
        this.positions[i].name = investment.fundName;
        return;
      }
    }

    this.positions.push({
      productId: investment.fundId,
      name: investment.fundName,
      amount: investment.amount,
      investments: [investment],
      stage: this.stageMap.get(investment.fundId),
      fund: this.getFund(investment.fundId),
    });
  }

  putInvestmentStage(investment: TransactionType) {
    var fund = this.getFund(investment.fundId);
    var investmentStage = fund?.investmentStage + "";
    switch (investmentStage) {
      case "EARLY_AND_GROWTH":
        investmentStage = "Früh- und Wachstumsphase";
        break;
      case "SEED":
        investmentStage = "Frühphase";
        break;
      case "GROWTH":
        investmentStage = "Wachstumsphase";
    }

    //update the entry for the specific stage
    for (var i = 0; i < this.positionsByStage.length; i++) {
      if (this.positionsByStage[i].stage == investmentStage) {
        this.positionsByStage[i].amount += investment.amount;
        this.positionsByStage[i].stage = investmentStage;
        return;
      }
    }

    this.positionsByStage.push({
      stage: investmentStage,
      amount: investment.amount,
    });
  }
}
